import { TargetingValues } from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryProviderPortal/apis';
import { defaultTargetingParameters } from '@provider-portal/common/services/targetingDefaults';

import type { MixpanelReplayDecision } from '@provider-portal/common/services/MixpanelReplayController';
import { createSelector } from 'reselect';

import { NetworkRequestState, selectIsUserLoggedIn } from '@bolteu/redux-session';

import { selectAccountId, selectLogoutRequestState } from 'rdx/session/selectors';
import { GlobalAppState } from 'rdx/state';

import type { TargetingSessionContext } from './actions';

export const selectIsTargetingParametersRequestFinished = (state: GlobalAppState) => {
  return (
    state.targetingParameters.fetchingState === NetworkRequestState.success ||
    state.targetingParameters.fetchingState === NetworkRequestState.failure ||
    (state.targetingParameters.fetchingState === NetworkRequestState.waiting &&
      state.targetingParameters.isDoingSafeRefresh &&
      (!!state.targetingParameters.parameters || state.targetingParameters.hasCompletedRequest))
  );
};

export const selectTargetingParameters = (state: GlobalAppState): TargetingValues => {
  return state.targetingParameters.parameters ?? defaultTargetingParameters;
};

export const selectTargetingSessionContext = createSelector(
  [
    selectIsUserLoggedIn,
    selectAccountId,
    (state: GlobalAppState) => state.session.sessionId,
    selectLogoutRequestState,
  ],
  (loggedIn, accountId, sessionId, logoutState): TargetingSessionContext | null => {
    if (
      !loggedIn ||
      accountId === undefined ||
      !sessionId ||
      logoutState === NetworkRequestState.waiting
    ) {
      return null;
    }
    return { accountId, sessionId };
  },
);

export function sameTargetingSession(
  left: TargetingSessionContext | null,
  right: TargetingSessionContext | null,
): boolean {
  return left?.accountId === right?.accountId && left?.sessionId === right?.sessionId;
}

export const selectMixpanelReplayDecision = createSelector(
  [(state: GlobalAppState) => state.targetingParameters, selectTargetingSessionContext],
  (targeting, currentSession): MixpanelReplayDecision | null => {
    if (
      currentSession === null ||
      !sameTargetingSession(targeting.confirmedSession, currentSession) ||
      !targeting.parameters
    ) {
      return null;
    }
    const enabled: unknown =
      targeting.parameters.delivery_provider_portal_flags?.mixpanel_session_replay_enabled;
    return {
      distinctId: `provider-portal-account-${currentSession.accountId}`,
      sessionId: currentSession.sessionId,
      enabled: enabled === true,
    };
  },
);
